import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PartyDetails from './steps/PartyDetails';
import ReviewSign from './steps/ReviewSign';
import StripeBilling from './steps/StripeBilling';
import PaymentDetails from './steps/PaymentDetails';
import AustracDocuments from './steps/AustracDocuments';
import GoLiveC1 from './steps/GoLiveC1';
import { 
  resumeRegistrationSession, 
  saveRegistrationProgress,
  RegistrationStep
} from '../../services/registrationSessionService';
import './C1SignupFlow.css';
import { LoadingSpinner } from '../shared';

export interface C1SignupData {
  // Step 1: Party Details
  partyName?: string;
  partyAbn?: string;
  partyAcn?: string;
  partyType?: 'sole-traders' | 'partnerships-or-associations' | 'body-corporates-pty' | 'trusts' | 'government-bodies';
  partyEmail?: string;
  partyEntityType?: string;     // e.g. "Australian Private Company"
  partyAddress?: string;        // address from ABR lookup
  partySector?: string;         // AUSTRAC reporting entity sector
  adminPhone?: string;
  adminFirstName?: string;      // Admin user first name
  adminLastName?: string;       // Admin user last name

  // Step 2: Review & Sign
  reviewConfirmed?: boolean;
  signatureData?: string;
  agreedToTerms?: boolean;
  signatureAccepted?: boolean;
  uploadedSignature?: string;
  drawnSignature?: string;
  signatureMode?: 'typed' | 'drawn' | 'upload';
  signedAgreementPdfBase64?: string;  // base64 PDF for S3 upload (legacy)
  signedAgreementPdfS3Key?: string;   // S3 key after direct presigned PUT upload

  // Step 3: Billing - Credit Packages
  stripePlanType?: 'STARTER' | 'GROWTH' | 'ENTERPRISE' | 'OFFER';
  billingCycle?: 'one-time';
  paymentMethod?: 'card' | 'bank';
  creditsPurchased?: number;
  totalAmount?: number;

  // Step 4: Payment — all methods handled by Stripe PaymentElement
  stripePaymentMethodId?: string;
  stripeBillingEmail?: string;

  // Step 5: Begin CDD Coverage
  goLiveStatus?: 'pending' | 'approved' | 'rejected';

  // Navigation flags
  skipToGoLive?: boolean;
}

type SignupStep = 'party-details' | 'review' | 'billing' | 'payment-details' | 'austrac-docs' | 'go-live';

interface StepConfig {
  key: SignupStep;
  title: string;
  description: string;
  component: React.ComponentType<any>;
  requiresCompletion: boolean;
}

const INITIAL_SIGNUP_DATA: C1SignupData = {};

// Local storage key for persisting form data
const SIGNUP_STORAGE_KEY = 'c1_signup_form_data';

const C1SignupFlow: React.FC = () => {
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [signupData, setSignupData] = useState<C1SignupData>(INITIAL_SIGNUP_DATA);
  const [completedSteps, setCompletedSteps] = useState<Set<SignupStep>>(new Set());
  const [showLossWarning, setShowLossWarning] = useState(false);
  const [isResuming, setIsResuming] = useState(true);
  const [showResumeToast, setShowResumeToast] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const progressStepsRef = useRef<HTMLDivElement>(null);

  // Map step key to index
  const getStepIndex = useCallback((stepKey: string): number => {
    const stepMap: Record<string, number> = {
      'party-details': 0,
      'review': 1,
      'billing': 2,
      'payment-details': 3,
      'agreement-sign': 3,
      'austrac-docs': 4,
      'go-live': 5,
    };
    return stepMap[stepKey] ?? 0;
  }, []);

  // Session recovery on mount
  useEffect(() => {
    const attemptResume = async () => {
      try {
        // Check if this is a payment redirect (from Klarna, etc.)
        const urlParams = new URLSearchParams(window.location.search);
        const isPaymentRedirect = urlParams.get('payment_redirect') === 'true' || 
                                   urlParams.has('payment_intent_client_secret') ||
                                   urlParams.has('redirect_status');

        const response = await resumeRegistrationSession();
        
        if (response.found && response.session) {
          
          // Merge session data into signup data
          const mergedData: C1SignupData = {
            ...INITIAL_SIGNUP_DATA,
            ...response.session.businessDetails,
            ...response.session.reviewSign,
            ...response.session.planSelection,
            ...response.session.paymentDetails,
          };
          
          setSignupData(mergedData);
          
          // If this is a payment redirect, force navigate to payment-details step
          // Otherwise, navigate to the saved step
          const stepIndex = isPaymentRedirect 
            ? getStepIndex('payment-details')
            : getStepIndex(response.session.currentStep);
          setCurrentStepIndex(stepIndex);
          
          // Mark previous steps as completed
          const stepsToComplete: SignupStep[] = ['party-details', 'review', 'billing', 'payment-details', 'austrac-docs', 'go-live'];
          const completedSet = new Set<SignupStep>();
          for (let i = 0; i < stepIndex; i++) {
            completedSet.add(stepsToComplete[i]);
          }
          setCompletedSteps(completedSet);
          
          // Show resume toast (but not for payment redirects - they handle their own flow)
          if (!isPaymentRedirect) {
            setShowResumeToast(true);
            setTimeout(() => setShowResumeToast(false), 5000);
          }
          
        } else if (isPaymentRedirect) {
          // Payment redirect but no session found - try to recover from localStorage
          const savedData = localStorage.getItem(SIGNUP_STORAGE_KEY);
          if (savedData) {
            try {
              const parsedData = JSON.parse(savedData) as C1SignupData;
              setSignupData(parsedData);
              setCurrentStepIndex(getStepIndex('payment-details'));
              
              // Mark previous steps as completed
              const completedSet = new Set<SignupStep>(['party-details', 'review', 'billing']);
              setCompletedSteps(completedSet);
            } catch {
              // Invalid localStorage data
              localStorage.removeItem(SIGNUP_STORAGE_KEY);
            }
          }
        } else {
          // Clear any stale local storage
          localStorage.removeItem(SIGNUP_STORAGE_KEY);
        }
      } catch (error) {
        localStorage.removeItem(SIGNUP_STORAGE_KEY);
      } finally {
        setIsResuming(false);
      }
    };
    
    attemptResume();
  }, [getStepIndex]);

  // Persist signup data to localStorage whenever it changes
  useEffect(() => {
    if (!isResuming) {
      try {
        localStorage.setItem(SIGNUP_STORAGE_KEY, JSON.stringify(signupData));
      } catch (error) {
      }
    }
  }, [signupData, isResuming]);

  // Auto-scroll active step into view
  useEffect(() => {
    if (progressStepsRef.current) {
      const activeStep = progressStepsRef.current.querySelector('[data-testid^="progress-step-"].active');
      if (activeStep) {
        activeStep.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [currentStepIndex]);

  // Dynamically build step sequence based on payment method
  const getStepSequence = (): StepConfig[] => {
    return [
      {
        key: 'party-details',
        title: 'Party Details',
        description: 'Tell us about your organization',
        component: PartyDetails,
        requiresCompletion: true,
      },
      {
        key: 'review',
        title: 'Review & Sign',
        description: 'Review and sign the agreement',
        component: ReviewSign,
        requiresCompletion: true,
      },
      {
        key: 'billing',
        title: 'Billing & Payment',
        description: 'Select your billing plan and payment method',
        component: StripeBilling,
        requiresCompletion: true,
      },
      {
        key: 'payment-details',
        title: 'Payment Details',
        description: 'Complete your payment information',
        component: PaymentDetails,
        requiresCompletion: true,
      },
      {
        key: 'austrac-docs',
        title: 'AUSTRAC Documents',
        description: 'Review and download compliance documents',
        component: AustracDocuments,
        requiresCompletion: true,
      },
      {
        key: 'go-live',
        title: 'Begin CDD Coverage',
        description: 'Activate your CDD coverage',
        component: GoLiveC1,
        requiresCompletion: false,
      },
    ];
  };

  // Recalculate step sequence whenever payment method changes
  const STEP_SEQUENCE = getStepSequence();
  
  // No need for payment method mismatch detection anymore
  // since all payment methods go through the same unified 'payment-details' step

  const currentStep = STEP_SEQUENCE[currentStepIndex];
  const CurrentComponent = currentStep?.component;

  // Check if signup is completed (go-live step is completed)
  const isSignupCompleted = completedSteps.has('go-live');

  const handleNext = async (stepData: Partial<C1SignupData>) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
    // Extract skipToGoLive flag if present
    const { skipToGoLive, ...dataToSave } = stepData;
    
    // Merge new data with existing data
    const newSignupData = { ...signupData, ...dataToSave };
    setSignupData(newSignupData);
    
    // Determine the next step
    let nextStepIndex: number;
    if (skipToGoLive) {
      nextStepIndex = STEP_SEQUENCE.findIndex(step => step.key === 'go-live');
    } else {
      nextStepIndex = currentStepIndex + 1;
    }
    const nextStep = STEP_SEQUENCE[nextStepIndex];
    
    // Save progress to server (if we have email to identify the session)
    // Save CURRENT step data with current step key, and set currentStep to next step
    if (newSignupData.partyEmail) {
      try {
        // Save current step data and update currentStep to next step in one call
        await saveRegistrationProgress(
          currentStep.key as RegistrationStep,
          dataToSave,  // Only send the step-specific data
          nextStep?.key as RegistrationStep  // Set currentStep to next step
        );
      } catch (error) {
        // Continue anyway - localStorage backup is in place
      }
    }
    
    setCompletedSteps(prev => {
      const newSet = new Set(prev);
      newSet.add(currentStep.key);
      
      // If skipping to go-live, also mark go-live as completed
      if (skipToGoLive) {
        newSet.add('go-live');
      }
      
      return newSet;
    });

    // If skipToGoLive is true, jump to the Go Live step
    if (skipToGoLive) {
      const goLiveIndex = STEP_SEQUENCE.findIndex(step => step.key === 'go-live');
      if (goLiveIndex !== -1) {
        setCurrentStepIndex(goLiveIndex);
      }
    } else if (currentStepIndex < STEP_SEQUENCE.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
    }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrevious = () => {
    
    // If user has entered any form data (completed any steps), show warning
    if (completedSteps.size > 0) {
      setShowLossWarning(true);
    } else if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  const handleConfirmGoBack = () => {
    // User confirmed they want to go back and start over
    setShowLossWarning(false);
    
    // Reset all form data
    setSignupData(INITIAL_SIGNUP_DATA);
    
    // Clear completed steps
    setCompletedSteps(new Set());
    
    // Go back to first step
    setCurrentStepIndex(0);
  };

  const handleCancelGoBack = () => {
    // User decided not to go back
    setShowLossWarning(false);
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel the signup process?')) {
      navigate('/login');
    }
  };

  // Handle session found via email lookup (cross-browser recovery)
  const handleSessionFoundByEmail = useCallback((sessionData: {
    currentStep: string;
    businessDetails?: Partial<C1SignupData>;
    reviewSign?: Partial<C1SignupData>;
    planSelection?: Partial<C1SignupData>;
    paymentDetails?: Partial<C1SignupData>;
  }) => {
    
    // Merge session data into signup data
    const mergedData: C1SignupData = {
      ...INITIAL_SIGNUP_DATA,
      ...sessionData.businessDetails,
      ...sessionData.reviewSign,
      ...sessionData.planSelection,
      ...sessionData.paymentDetails,
    };
    
    setSignupData(mergedData);
    
    // Navigate to the saved step
    const stepIndex = getStepIndex(sessionData.currentStep);
    setCurrentStepIndex(stepIndex);
    
    // Mark previous steps as completed
    const stepsToComplete: SignupStep[] = ['party-details', 'review', 'billing', 'payment-details', 'austrac-docs', 'go-live'];
    const completedSet = new Set<SignupStep>();
    for (let i = 0; i < stepIndex; i++) {
      completedSet.add(stepsToComplete[i]);
    }
    setCompletedSteps(completedSet);
    
    // Show resume toast
    setShowResumeToast(true);
    setTimeout(() => setShowResumeToast(false), 5000);
    
  }, [getStepIndex]);

  // Hide Previous button on the final step (Begin CDD Coverage)
  const canGoBack = currentStepIndex > 0 && currentStep?.key !== 'go-live';

  React.useEffect(() => {
  }, [showLossWarning, isSignupCompleted, completedSteps]);

  return (
    <div className="c1-signup-container">
      {/* Resume Session Toast */}
      {showResumeToast && (
        <div 
          className="resume-toast"
          data-testid="resume-session-toast"
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: '#4caf50',
            color: 'white',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'slideIn 0.3s ease-out',
          }}
        >
          <span style={{ fontSize: '20px' }}>✓</span>
          <div>
            <div style={{ fontWeight: 600 }}>Session Restored</div>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>
              Your previous progress has been restored
            </div>
          </div>
        </div>
      )}

      {/* Loading state while checking for existing session */}
      {isResuming ? (
        <div 
          className="session-loading"
          data-testid="session-loading"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            gap: '16px',
          }}
        >
          <div 
            className="loading-spinner"
            style={{
              width: '48px',
              height: '48px',
              border: '4px solid #e0e0e0',
              borderTop: '4px solid #1976d2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ color: '#666', fontSize: '16px' }}>Checking for existing session...</p>
        </div>
      ) : (
        <>
      {/* Loss Progress Warning Modal */}
      {showLossWarning && (
        <div className="modal-overlay" data-testid="loss-warning-modal-overlay">
          <div className="modal-content loss-warning-modal" data-testid="loss-warning-modal-content">
            <div className="modal-header" data-testid="loss-warning-modal-header">
              <h3>⚠️ Warning: Lose All Progress</h3>
            </div>
            <div className="modal-body" data-testid="loss-warning-modal-body">
              <p>
                Going back will clear all your saved progress in this form, 
                and you'll have to start over from the beginning.
              </p>
              <p style={{ marginTop: '12px', fontWeight: 600, color: '#d32f2f' }}>
                Are you sure you want to continue?
              </p>
            </div>
            <div className="modal-footer" data-testid="loss-warning-modal-footer">
              <button className="btn-primary" data-testid="loss-warning-stay-btn" onClick={handleCancelGoBack}>
                Stay on Current Step
              </button>
              <button className="btn-secondary btn-danger" data-testid="loss-warning-confirm-btn" onClick={handleConfirmGoBack}>
                Yes, Go Back & Start Over
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="c1-signup-wrapper">
        {/* Submitting Overlay */}
        {isSubmitting && (
          <div className="submitting-overlay" data-testid="submitting-overlay">
            <div className="submitting-overlay__content">
              <img
                src="/arcaml_shield_logo.png"
                alt="ARCaml"
                className="submitting-overlay__logo"
              />
              <LoadingSpinner size={40} color="#1B3A6B" trackColor="rgba(27,58,107,0.15)" thickness={4} />
              <p className="submitting-overlay__text">Saving your progress…</p>
            </div>
          </div>
        )}
        {/* Left Sidebar - Header & Progress */}
        <div className="c1-signup-header" data-testid="c1-signup-header">
          <div className="header-content">
            <h1>🚀 ARCaml</h1>
            <p>Complete your subscription in {STEP_SEQUENCE.length} simple steps</p>
            
            {/* Progress Bar */}
            <div className="c1-signup-progress" data-testid="c1-signup-progress">
              <div className="progress-bar-container" data-testid="progress-bar-container">
                <div className="progress-bar-fill" data-testid="progress-bar-fill" style={{ width: `${((currentStepIndex + 1) / STEP_SEQUENCE.length) * 100}%` }}></div>
              </div>
              <div className="progress-text" data-testid="progress-text">Step {currentStepIndex + 1} of {STEP_SEQUENCE.length}</div>
              <div className="progress-steps" data-testid="progress-steps" ref={progressStepsRef}>
                {STEP_SEQUENCE.map((step, index) => (
                  <div
                    key={step.key}
                    className={`progress-step ${
                      step.key === currentStep.key ? 'active' : ''
                    } ${
                      completedSteps.has(step.key) ? 'completed' : ''
                    } ${
                      STEP_SEQUENCE.indexOf(step) < currentStepIndex ? 'past' : ''
                    }`}
                    data-testid={`progress-step-${step.key}`}
                  >
                    <div className="step-number">
                      {completedSteps.has(step.key) ? '✓' : index + 1}
                    </div>
                    <div className="step-label">{step.title}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Content Area */}
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, height: '100%' }} data-testid="c1-signup-content-area">
          {/* Step Content */}
          <div className="c1-signup-content" data-testid="c1-signup-content">
            <div className="step-header" data-testid="step-header">
              <h2>{currentStep.title}</h2>
              <p>{currentStep.description}</p>
            </div>

            <div className="step-component" data-testid={`step-component-${currentStep.key}`}>
              <CurrentComponent
                data={signupData}
                onNext={handleNext}
                onCancel={handleCancel}
                onSessionFoundByEmail={handleSessionFoundByEmail}
              />
            </div>
          </div>

          {/* Navigation Controls - Fixed at bottom */}
          <div className="c1-signup-footer" data-testid="c1-signup-footer">
            <div className="footer-left">
              {canGoBack && (
                <button
                  className="btn-secondary"
                  data-testid="previous-step-btn"
                  onClick={handlePrevious}
                >
                  ← Previous
                </button>
              )}
            </div>

            <div className="footer-right">
              {/* Navigation buttons are handled within individual step components */}
            </div>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default C1SignupFlow;
